import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

function initialsOf(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function colorFromName(fullName: string): string {
  // Stable hue from name — keeps initials avatars colorful but deterministic.
  let hash = 0;
  for (let i = 0; i < fullName.length; i++) {
    hash = (hash * 31 + fullName.charCodeAt(i)) >>> 0;
  }
  const hue = hash % 360;
  return `hsl(${hue} 55% 45%)`;
}

interface EmployeeAvatarProps {
  fullName: string;
  photoUrl?: string | null;
  size?: number;
  className?: string;
}

export function EmployeeAvatar({ fullName, photoUrl, size = 40, className }: EmployeeAvatarProps) {
  const [failed, setFailed] = useState(false);
  // Reset the failed flag when photoUrl changes — without this, switching the
  // photo on the same component instance keeps the prior URL's failure state
  // and stays in initials-mode forever. biome's useExhaustiveDependencies
  // wants to drop photoUrl because the body only references `setFailed`
  // (which is stable), but the ENTIRE point of this effect is to react to
  // photoUrl changes — so the dep is intentional.
  // biome-ignore lint/correctness/useExhaustiveDependencies: photoUrl is the trigger, not consumed inside
  useEffect(() => {
    setFailed(false);
  }, [photoUrl]);
  const showImage = photoUrl && !failed;

  return (
    <div
      role="img"
      className={cn(
        "inline-flex items-center justify-center rounded-full overflow-hidden text-white font-medium select-none shrink-0",
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: Math.max(10, Math.round(size * 0.38)),
        backgroundColor: showImage ? "transparent" : colorFromName(fullName),
      }}
      aria-label={fullName}
    >
      {showImage ? (
        <img
          src={photoUrl}
          alt={fullName}
          width={size}
          height={size}
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <span>{initialsOf(fullName)}</span>
      )}
    </div>
  );
}
